import { memo, useMemo, Suspense, useEffect, useState } from "react";
import { DashboardSidebar } from "@/components/dashboard/dashboard-sidebar";
import { Header } from "./header";
import { useLocation } from "react-router-dom";
import { useAuthToken } from "@/context/AuthTokenContext";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useTranslation } from "@/hooks/useTranslation";
import { checkProjectStatus } from "@/services/projectApi";

// Page-level loading skeleton
const PageSkeleton = () => (
  <div className="p-6 space-y-6 animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-48"></div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 bg-gray-200 rounded"></div>
        ))}
      </div>
      <div className="md:col-span-2 space-y-4">
        <div className="h-32 bg-gray-200 rounded"></div>
        <div className="h-48 bg-gray-200 rounded"></div>
      </div>
    </div>
  </div>
);

// Memoized StatusBar component to avoid recreation on every render
const StatusBar = memo(({ isBlocked, lastChecked, t }) => {
  // Avoid hydration mismatch: render placeholder on server and until mounted
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const formattedTime = useMemo(() => {
    if (!mounted || !lastChecked) return "--:--:--";
    try {
      // Fixed locale/format to be consistent across devices
      return new Date(lastChecked).toLocaleTimeString("en-GB", {
        hour12: false,
      });
    } catch (e) {
      return "--:--:--";
    }
  }, [mounted, lastChecked]);

  return (
    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div
              className={`w-2 h-2 rounded-full ${
                !isBlocked ? "bg-green-500" : "bg-red-500"
              }`}
            ></div>
            <span className="text-sm font-medium text-gray-700">
              {!isBlocked
                ? t("header.Online", "Online")
                : t("header.Offline", "Offline")}
            </span>
          </div>
          {isBlocked && (
            <div className="text-sm text-gray-500">
              {t(
                "header.BlockedMessage",
                "Services are temporarily unavailable. Please check your usage limits or contact support for assistance."
              )}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-xs text-gray-400">
            {t("header.LastChecked", "Last checked")}: {formattedTime}
          </div>
        </div>
      </div>
    </div>
  );
});

StatusBar.displayName = "StatusBar";

export const AppLayout = memo(({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;
  const { isAuthReady } = useAuthToken();
  const [isBlocked, setIsBlocked] = useState(false);
  const [lastChecked, setLastChecked] = useState(0);

  const activePage = useMemo(() => {
    if (pathname === "/") return "analytics";
    if (pathname?.startsWith("/agents")) return "agents";
    if (pathname?.startsWith("/messaging")) return "messaging";
    if (pathname?.startsWith("/account")) return "account";
    return "analytics";
  }, [pathname]);

  // Check if project is active - only after auth is ready
  useEffect(() => {
    // Don't check until auth client is ready
    if (!isAuthReady) {
      return;
    }

    const checkUserStatus = async () => {
      try {
        const projectActive = await checkProjectStatus();
        // User is blocked if project is inactive
        setIsBlocked(!projectActive);
        setLastChecked(Date.now());
      } catch (error) {
        console.error("Error checking project status:", error);
        setLastChecked(Date.now());
      }
    };

    checkUserStatus();
  }, [pathname, isAuthReady]); // Re-check when pathname changes or auth state changes

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <DashboardSidebar activePage={activePage} />
        <SidebarInset>
          <Header />
          <StatusBar isBlocked={isBlocked} lastChecked={lastChecked} t={t} />
          <main className="flex-1 p-4 md:p-6">
            <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
});

AppLayout.displayName = "AppLayout";
